import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import * as c from "../../../constants";
import ButtonForm from "../../../components/Button/ButtonForm";
import ActionHeader from "./ActionHeader";

const STATUS_OPTIONS = ["yes", "partially", "no"];
const STATUS_LABEL_KEYS = {
  yes: "lab_checkin_status_yes",
  partially: "lab_checkin_status_partial",
  no: "lab_checkin_status_no",
};

export default function RecommendationsCheckIn() {
  const theme = useTheme();
  const { state } = useAuth();
  const { t } = useTranslation();
  const userId = state.user?.id;

  const [loading, setLoading] = useState(true);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [responses, setResponses] = useState({}); // order -> { status, note }
  const [additionalActions, setAdditionalActions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── Fetch most recent report + any existing check-in ──────────────────────
  useEffect(() => {
    if (!userId) return;
    let mounted = true;

    const init = async () => {
      try {
        // Newest-first list of dates that actually have a generated report —
        // more reliable than deriving "most recent" from raw measurement dates,
        // which aren't guaranteed to line up with an existing report's analysis_date.
        const { data: reportDates } = await axios.get(
          `${c.API_URL}/api/reports/${userId}/`
        );
        if (!reportDates?.length) {
          if (mounted) setLoading(false);
          return;
        }
        const date = reportDates[0].analysis_date;

        const { data: report } = await axios.get(
          `${c.API_URL}/api/reports/${userId}/${date}/`
        );
        if (!mounted) return;

        const recs = [...(report.top_recommendations || [])].sort(
          (a, b) => a.order - b.order
        );
        setAnalysisDate(report.analysis_date);
        setRecommendations(recs);

        const initialResponses = {};
        recs.forEach((r) => {
          initialResponses[r.order] = { status: null, note: "" };
        });

        try {
          const { data: checkin } = await axios.get(
            `${c.API_URL}/api/reports/${userId}/${date}/check-in/`
          );
          if (mounted) {
            checkin.responses?.forEach((r) => {
              initialResponses[r.recommendation_order] = {
                status: r.status,
                note: r.note || "",
              };
            });
            setAdditionalActions(checkin.additional_actions || "");
          }
        } catch {
          // no prior check-in — start blank
        }

        if (mounted) setResponses(initialResponses);
      } catch {
        // no report / no measurements yet — silently hide the section
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const setStatus = (order, status) =>
    setResponses((prev) => ({ ...prev, [order]: { ...prev[order], status } }));

  const setNote = (order, note) =>
    setResponses((prev) => ({ ...prev, [order]: { ...prev[order], note } }));

  const allAnswered =
    recommendations.length > 0 &&
    recommendations.every((r) => responses[r.order]?.status);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post(`${c.API_URL}/api/reports/${userId}/${analysisDate}/check-in/`, {
        responses: recommendations.map((r) => ({
          recommendation_order: r.order,
          status: responses[r.order]?.status,
          note: responses[r.order]?.note || "",
        })),
        additional_actions: additionalActions,
      });
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.response?.data?.message || t("val_server_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !recommendations.length) return null;

  const outerCardSx = {
    border: "1px solid",
    borderRadius: 4,
    p: { xs: 2, sm: 3 },
    width: "100%",
    ...(theme.palette.mode === "light"
      ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
      : {
          bgcolor: "greydark.C980",
          borderColor: "greydark.C400",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        }),
  };

  const innerCardSx = {
    border: "1px solid",
    borderRadius: 3,
    p: 2.5,
    borderColor: theme.palette.mode === "light" ? "grey.C900" : "greydark.C400",
    bgcolor: theme.palette.mode === "light" ? "grey.C10" : "greydark.C800",
  };

  const formattedDate = analysisDate ? dayjs(analysisDate).format("D. MMMM YYYY") : "";

  return (
    <Box sx={outerCardSx}>
      <Box sx={{ mb: 3 }}>
        <ActionHeader
          number={2}
          label={t("lab_action2_header")}
          accentColor={theme.palette.quartiary.C300}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("lab_checkin_subtitle")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {recommendations.map((rec, idx) => {
          const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;
          const response = responses[rec.order] || { status: null, note: "" };

          return (
            <Box key={rec.order} sx={innerCardSx}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: `2px solid ${accentColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: "2px",
                  }}
                >
                  <Typography
                    variant="number4"
                    sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
                  >
                    {idx + 1}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={700}>
                    {rec.what}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("lab_checkin_recommended_on")} {formattedDate}
                  </Typography>
                </Box>
              </Box>

              <Divider
                sx={{
                  my: 2,
                  borderColor: theme.palette.mode === "light" ? "grey.C900" : "greydark.C400",
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                  {t("lab_checkin_question")}
                </Typography>

                <Stack spacing={0} alignItems="flex-end">
                  {STATUS_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      labelPlacement="start"
                      label={t(STATUS_LABEL_KEYS[opt])}
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={response.status === opt}
                          onChange={() => setStatus(rec.order, opt)}
                          sx={{
                            "&.Mui-checked": {
                              color: theme.palette.mode === "light" ? "greydark.C900" : "grey.C200",
                            },
                          }}
                        />
                      }
                    />
                  ))}
                </Stack>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder={t("lab_checkin_note_placeholder")}
                value={response.note}
                onChange={(e) => setNote(rec.order, e.target.value)}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ ...innerCardSx, mt: 2 }}>
        <Typography variant="body1" fontWeight={700}>
          {t("lab_checkin_other_title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("lab_checkin_other_subtitle")}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder={t("lab_checkin_other_placeholder")}
          value={additionalActions}
          onChange={(e) => setAdditionalActions(e.target.value)}
        />
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}

      {submitSuccess ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t("lab_checkin_submit_success")}
        </Alert>
      ) : (
        <ButtonForm
          name={submitting ? <CircularProgress size={18} color="inherit" /> : t("lab_checkin_submit")}
          onClick={handleSubmit}
          disabled={submitting || !allAnswered}
        />
      )}
    </Box>
  );
}
